import React, { Component } from 'react';
import { Card, Table, Badge, Container, Row, Col, Alert } from 'react-bootstrap';
import { areFeaturesEnabled, refreshFallbackFlags } from '../shared/services/featureToggle/FeatureToggleManager';
import featureToggleManager from '../shared/services/featureToggle/FeatureToggleManager';
import { isAdminRole } from '../../redux/actions/userAction';
import './FeatureFlagDashboard.css';

/**
 * FeatureFlagDashboard - Admin interface for monitoring feature flags
 * 
 * Provides real-time visibility into feature flag status, performance metrics,
 * fallback status, and debugging information for administrators.
 */

// Import all feature flag constants
import { FEATURE_TOGGLE_KEYS } from '../shared/constants/FeatureToggleKeys';

class FeatureFlagDashboard extends Component {
  constructor(props) {
    super(props);
    
    this.state = {
      flags: {},
      loading: true,
      lastUpdated: new Date(),
      error: null,
      fallbackStatus: null
    };

    // Generate feature flags list dynamically from FEATURE_TOGGLE_KEYS
    this.featureFlags = Object.entries(FEATURE_TOGGLE_KEYS).map(([key, value]) => ({
      key: value,
      name: this.formatFlagName(key),
      keyName: key
    }));
  }

  // Helper method to format flag names from constants
  formatFlagName = (constantName) => {
    return constantName
      .replace(/^CPM_/, '') // Remove CPM_ prefix
      .replace(/_/g, ' ') // Replace underscores with spaces
      .toLowerCase()
      .replace(/\b\w/g, l => l.toUpperCase()); // Capitalize each word
  }

  componentDidMount() {
    // Check if user has admin access
    if (!isAdminRole()) {
      this.setState({
        error: 'Access Denied: Admin privileges required to view feature flags',
        loading: false
      });
      return;
    }

    this.loadFeatureFlags();
    
    // Set up auto-refresh every 30 seconds
    this.refreshInterval = setInterval(() => {
      this.loadFeatureFlags();
    }, 30000);
  }

  componentWillUnmount() {
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval);
    }
  }

  loadFeatureFlags = () => {
    try {
      const flagKeys = this.featureFlags.map(flag => flag.key);
      const flags = areFeaturesEnabled(flagKeys);
      
      // Get fallback status from FeatureToggleManager
      const fallbackStatus = {
        ...this.props.fallbackStatus,
        ...featureToggleManager.getFallbackStatus() // Merge with manager status
      };
      
      this.setState({
        flags,
        fallbackStatus,
        loading: false,
        lastUpdated: new Date(),
        error: null
      });
    } catch (error) {
      this.setState({
        error: 'Failed to load feature flags',
        loading: false
      });
    }
  }

  refreshFlags = () => {
    this.setState({ loading: true });
    this.loadFeatureFlags();
  }

  // New method to refresh fallback flags from API
  refreshFallbackFromAPI = async () => {
    this.setState({ loading: true });
    try {
      await refreshFallbackFlags();
      this.loadFeatureFlags();
    } catch (error) {
      this.setState({
        error: 'Failed to refresh fallback flags from API',
        loading: false
      });
    }
  }

  getStatusBadge = (value) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Badge variant="success" className="flag-badge">
          <i className="fas fa-check-circle"></i> Enabled
        </Badge>
      ) : (
        <Badge variant="secondary" className="flag-badge">
          <i className="fas fa-times-circle"></i> Disabled
        </Badge>
      );
    } else {
      return value !== '0' ? (<Badge variant="success" className="flag-badge">
        {value}
      </Badge>) : (<Badge variant="secondary" className="flag-badge">
        {value}
      </Badge>);
    }
  }

  getRowClass = (enabled) => {
    return enabled ? 'flag-row-enabled' : 'flag-row-disabled';
  }

  render() {
    const { flags, loading, lastUpdated, error, fallbackStatus } = this.state;

    if (error) {
      return (
        <Container className="mt-4">
          <Alert variant="danger">
            <Alert.Heading>Error</Alert.Heading>
            <p>{error}</p>
          </Alert>
        </Container>
      );
    }

    const enabledCount = Object.values(flags).filter(Boolean).length;
    const totalCount = this.featureFlags.length;

    return (
      <Container fluid className="feature-flag-dashboard">
        <Row className="mb-4">
          <Col>
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <div>
                  <h4 className="mb-0">
                    Feature Flag Dashboard
                  </h4> 
                  {/* Fallback Status Indicator */}
                  {fallbackStatus && fallbackStatus.isActive && (
                    <Badge variant="warning" className="ml-2">
                      <i className="fas fa-exclamation-triangle"></i> Fallback Mode
                    </Badge>
                  )}
                </div>
                <div>
                  <button 
                    className="btn btn-outline-primary btn-sm mr-2"
                    onClick={this.refreshFlags}
                    disabled={loading}
                  >
                    <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
                    {loading ? ' Loading...' : ' Refresh'}
                  </button>
                  {/* API refresh button */}
                  {fallbackStatus && fallbackStatus.isActive && (
                    <button 
                      className="btn btn-outline-warning btn-sm"
                      onClick={this.refreshFallbackFromAPI}
                      disabled={loading}
                      title="Refresh fallback flags from Parameter Store API"
                    >
                      <i className={`fas fa-cloud-download-alt ${loading ? 'fa-spin' : ''}`}></i>
                      API Refresh
                    </button>
                  )}
                </div>
              </Card.Header>
              <Card.Body>
                {/* Fallback Status Alert */}
                {fallbackStatus && fallbackStatus.isActive && (
                  <Alert variant="warning" className="mb-3">
                    <Alert.Heading>
                      <i className="fas fa-exclamation-triangle"></i> Using Fallback Values
                    </Alert.Heading>
                    <p>
                      LaunchDarkly is currently unavailable. Feature flags are being served from Parameter Store API fallback values.
                    </p>
                    <hr />
                    <div className="row">
                      <div className="col-md-6">
                        <small>
                          <strong>Last API Fetch:</strong><br />
                          {fallbackStatus.timestamp ? 
                            new Date(fallbackStatus.timestamp).toLocaleString() : 'N/A'}
                        </small>
                      </div>
                      <div className="col-md-6">
                        <small>
                          <strong>Fallback Status:</strong><br />
                          Reconnect Attempts: {fallbackStatus.ldReconnectAttempts || 0}<br />
                          Monitoring: {fallbackStatus.isMonitoringReconnection ? 'Active' : 'Inactive'}
                        </small>
                      </div>
                    </div>
                  </Alert>
                )}

                <div className="table-responsive">


                  <Table striped bordered hover>
                    <thead className="thead-dark">
                      <tr>
                        <th>Flag Name</th>
                        <th>Status</th>
                        <th>Flag Key</th>
                        <th>Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {this.featureFlags.map((flag) => {

                        return (
                          <tr key={flag.key} className={this.getRowClass( flags[flag.key])}>
                            <td>
                              <strong>{flag.name}</strong>
                            </td>
                            <td>
                              {this.getStatusBadge(flags[flag.key])}
                            </td>
                            <td>
                              <code className="flag-key">{flag.key}</code>
                            </td>
                            <td>
                              {fallbackStatus && fallbackStatus.isActive ? (
                                <div>
                                  <Badge variant="secondary">
                                    {fallbackStatus.source === 'api' ? 'Parameter Store' : 'Mock Fallback'}
                                  </Badge>
                                  {fallbackStatus.timestamp && (
                                    <small className="d-block text-muted mt-1">
                                      <i className="fas fa-check-circle text-success"></i> Cached
                                    </small>
                                  )}
                                </div>
                              ) : (
                                <Badge variant="primary">LaunchDarkly</Badge>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>

                <div className="mt-3 d-flex justify-content-between align-items-center">
                  <small className="text-muted">
                    <i className="fas fa-clock mr-1"></i>
                    Last updated: {lastUpdated.toLocaleString()}
                  </small>
                  <small className="text-muted">
                    <i className="fas fa-sync mr-1"></i>
                    Auto-refresh every 30 seconds
                  </small>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Debug Information (development only) */}
        {process.env.NODE_ENV === 'development' && fallbackStatus && (
          <Row>
            <Col>
              <Card>
                <Card.Header>
                  <h6>Debug Information - Fallback Status</h6>
                </Card.Header>
                <Card.Body>
                  <pre className="debug-info" style={{fontSize: '12px', backgroundColor: '#f8f9fa', padding: '10px'}}>
                    {JSON.stringify(fallbackStatus, null, 2)}
                  </pre>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
      </Container>
    );
  }
}

export default FeatureFlagDashboard;